import { createContext, useMemo } from 'react';

import type { WebThemeV2 } from '~/interfaces/publication';
import { BORDER_RADIUS_OPTIONS, SHADOW_OPTIONS } from '~/utils/designOptions';

import type { WebThemeContextProps } from './types';

const WebThemeContext = createContext<WebThemeContextProps>({
  webTheme: {
    font_body: '',
    font_header: '',
    font_button: '',

    color_primary: '',
    color_text_on_primary: '',
    color_secondary: '',
    color_text_on_secondary: '',
    color_background: '',
    color_border: '',
    color_link: '',
    color_overline_text: '',
    color_heading: '',
    color_body: '',
    color_underline: '',

    radius: '',
    shadow: '',

    navbar_template: 'template-1',
    navbar_background_color: '',
    navbar_text_color: '',
    navbar_button_primary_background_color: '',
    navbar_button_primary_text_color: '',
    navbar_button_primary_border_color: '',
    navbar_button_primary_shadow: '',
    navbar_button_secondary_background_color: '',
    navbar_button_secondary_text_color: '',
    navbar_button_secondary_border_color: '',
    navbar_login_button_desktop_enabled: true,
    navbar_button_secondary_shadow: '',
    navbar_font_family: '',
    navbar_logo_width: '',

    subscribe_widget_template: 'template-1',
    subscribe_widget_button_background_color: '',
    subscribe_widget_button_text_color: '',
    subscribe_widget_font_family: '',
    subscribe_widget_border_enabled: false,
    subscribe_widget_border_color: '',
    subscribe_widget_email_icon_enabled: false,
    subscribe_widget_cta_text: '',
    subscribe_widget_placeholder_text: '',
    subscribe_widget_button_font_family: '',
    subscribe_widget_body_color: '',

    post_background_fill_enabled: false,
    post_background_color: '',
    post_border_radius: '',
    post_border_color: '',
    post_content_tags_enabled: false,
    post_content_tags_background_color: '',
    post_content_tags_text_color: '',
    post_insights_likes_comments_enabled: false,
    post_insights_time_fields_enabled: false,
    post_insights_color: '',
    post_premium_badge_background_color: '',
    post_premium_badge_text_color: '',
    post_heading_font_family: '',
    post_heading_font_color: '',
    post_description_font_family: '',
    post_description_font_color: '',
    post_authors_font_color: '',
    post_authors_show_image: true,

    footer_background_color: '',
    footer_text_color: '',
    footer_external_link_color: '',
    footer_external_link_underline_color: '',
    footer_logo_width: '',
  },
  classes: {
    radius: '',
    shadow: '',
  },
  isEnabled: false,
});
WebThemeContext.displayName = 'WebThemeContext';

interface Props {
  children: React.ReactNode;
  webTheme: WebThemeV2;
  isEnabled: boolean;
}

export const WebThemeProvider = ({ children, webTheme, isEnabled }: Props) => {
  const { radiusClass, shadowClass } = useMemo(() => {
    return {
      shadowClass: SHADOW_OPTIONS[webTheme?.shadow || 'none'],
      radiusClass: BORDER_RADIUS_OPTIONS[webTheme?.radius || 'none'],
    };
  }, [webTheme]);

  return (
    <WebThemeContext.Provider
      value={{
        webTheme,
        classes: {
          radius: radiusClass,
          shadow: shadowClass,
        },
        isEnabled,
      }}
    >
      {children}
    </WebThemeContext.Provider>
  );
};

// biome-ignore lint/style/useComponentExportOnlyModules: Context export pattern
export { WebThemeContext };
